import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Copy, RefreshCw, KeyRound, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const STATUS_TONE = {
  ready: "bg-emerald-100 text-emerald-800",
  duplicate: "bg-slate-100 text-slate-600",
  unmapped_group: "bg-amber-100 text-amber-800",
  unmapped_disposition: "bg-amber-100 text-amber-800",
  missing_datetime: "bg-rose-100 text-rose-800",
  error: "bg-rose-100 text-rose-800",
  pending: "bg-sky-100 text-sky-800",
};

export const WebhookStep = () => {
  const [creds, setCreds] = useState(null);
  const [teams, setTeams] = useState({});

  useEffect(() => {
    api.get("/admin/integrations/laaffic").then((r) => setCreds(r.data.items || []));
    api.get("/teams").then((r) => {
      const m = {};
      (r.data || []).forEach((t) => { m[t.id] = t.name; });
      setTeams(m);
    }).catch(() => {});
  }, []);

  if (!creds) return <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>;
  if (!creds.length) return <div className="text-xs text-amber-700">Add Laaffic credentials in step 1 first.</div>;

  return (
    <div className="space-y-4 text-xs" data-testid="webhook-step">
      <div className="text-slate-600 max-w-3xl">
        Paste the callback URL into Laaffic → Account settings → Callback Address → Voice →
        Configuration → <b>Cloud call center callback address</b>. Each finished call is pushed here,
        mapped to a brand by <b>agent group name</b> (= the campaign's SIP group name) and saved.
        We only reply 200 after the call is safely stored, so Laaffic retries (every 30 min, 3×) if we're down.
      </div>
      {creds.map((c) => <TeamWebhook key={c.team_id} teamId={c.team_id} teamName={teams[c.team_id]} />)}
    </div>
  );
};

function TeamWebhook({ teamId, teamName }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/integrations/laaffic/webhook/${teamId}`).then((r) => setData(r.data));
  }, [teamId]);
  useEffect(() => { load(); }, [load]);

  const url = data?.path ? `${window.location.origin}${data.path}` : null;

  const rotate = async () => {
    if (data?.configured && !window.confirm("Generate a new URL? The old one stops working immediately — you must update it in Laaffic.")) return;
    setBusy("rotate");
    try { await api.post(`/admin/integrations/laaffic/webhook/${teamId}/rotate`); toast.success("New callback URL generated"); load(); }
    finally { setBusy(""); }
  };
  const reprocess = async () => {
    setBusy("reprocess");
    try {
      const r = await api.post(`/admin/integrations/laaffic/webhook/${teamId}/reprocess`);
      toast.success(`Reprocessed: ${JSON.stringify(r.data.results)}`); load();
    } finally { setBusy(""); }
  };
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Callback URL copied"); };

  if (!data) return <div className="text-slate-500"><Loader2 className="w-3 h-3 animate-spin inline" /> Loading {teamName || teamId}…</div>;

  return (
    <div className="glass-card p-3 space-y-3" data-testid={`webhook-team-${teamId}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-slate-900">{teamName || teamId.slice(0, 8)}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid={`webhook-refresh-${teamId}`}><RefreshCw className="w-3 h-3" /></Button>
          {(data.counts?.error || data.counts?.pending) ? (
            <Button size="sm" variant="outline" onClick={reprocess} disabled={!!busy} data-testid={`webhook-reprocess-${teamId}`}>
              <RotateCcw className="w-3 h-3 mr-1" /> Retry stuck
            </Button>
          ) : null}
          <Button size="sm" variant={data.configured ? "outline" : "default"} onClick={rotate} disabled={!!busy} data-testid={`webhook-rotate-${teamId}`}>
            {busy === "rotate" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <KeyRound className="w-3 h-3 mr-1" />}
            {data.configured ? "Rotate URL" : "Generate callback URL"}
          </Button>
        </div>
      </div>
      {url && (
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-slate-50 border border-slate-200 rounded px-2 py-1.5 font-mono text-[11px] break-all" data-testid={`webhook-url-${teamId}`}>{url}</code>
          <Button size="sm" variant="outline" onClick={copy} data-testid={`webhook-copy-${teamId}`}><Copy className="w-3 h-3" /></Button>
        </div>
      )}
      <TzControl teamId={teamId} data={data} onSaved={load} />
      <WebhookCounts data={data} />
      <RecentPushes rows={data.recent} />
    </div>
  );
}

function WebhookCounts({ data }) {
  return (
    <div className="flex flex-wrap gap-2 text-[11px]" data-testid="webhook-counts">
      <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">{data.last_24h} in last 24h</span>
      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{data.total} total</span>
      {Object.entries(data.counts || {}).map(([k, v]) => (
        <span key={k} className={`px-2 py-0.5 rounded-full ${STATUS_TONE[k] || "bg-slate-100"}`}>{k}: {v}</span>
      ))}
    </div>
  );
}

function RecentPushes({ rows }) {
  if (!rows?.length) return <div className="text-slate-500" data-testid="webhook-no-pushes">No pushes received yet.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]" data-testid="webhook-recent-table">
        <thead className="bg-slate-50 text-slate-500 text-left">
          <tr>{["Received", "Call start", "Agent group", "Agent", "Number", "Tags", "Sec", "Status"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" title={r.error || ""}>
              <td className="px-2 py-1 tabular-nums">{new Date(r.received_at).toLocaleString()}</td>
              <td className="px-2 py-1 tabular-nums">{r.call_start || "—"}</td>
              <td className="px-2 py-1 font-mono">{r.agent_group || "—"}</td>
              <td className="px-2 py-1">{r.agent || "—"}</td>
              <td className="px-2 py-1 font-mono">{r.called || "—"}</td>
              <td className="px-2 py-1">{(r.labels || []).join(", ") || "—"}</td>
              <td className="px-2 py-1 tabular-nums">{r.duration ?? "—"}</td>
              <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded ${STATUS_TONE[r.status] || "bg-slate-100"}`}>{r.status}{r.push_count > 1 ? ` ×${r.push_count}` : ""}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const fmtOffset = (m) => {
  if (m == null) return "—";
  const sign = m >= 0 ? "+" : "-";
  const a = Math.abs(m);
  return `UTC${sign}${Math.floor(a / 60)}${a % 60 ? ":" + String(a % 60).padStart(2, "0") : ""}`;
};
const TZ_CHOICES = [330, 480, 0];

function TzControl({ teamId, data, onSaved }) {
  const [busy, setBusy] = useState(false);
  const cur = data.push_tz_offset_min;
  const det = data.tz_detection || {};
  const mismatch = det.detected_offset_min != null && det.detected_offset_min !== cur && det.samples >= 5;

  const apply = async (offset) => {
    if (!window.confirm(`Read this team's Laaffic push times as ${fmtOffset(offset)}? Calls already received will be re-timed.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/integrations/laaffic/webhook/${teamId}/timezone`, { offset_min: offset });
      toast.success(`Saved. Re-timed ${r.data.retimed_ops_rows} dashboard calls and ${r.data.retimed_unmapped_rows} waiting calls.`);
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded border px-2 py-1.5 flex flex-wrap items-center gap-2 ${mismatch ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`} data-testid={`webhook-tz-${teamId}`}>
      <span>Push times read as <b>{fmtOffset(cur)}</b>{cur === 330 ? " (IST)" : ""}</span>
      <span className="text-slate-500" data-testid={`webhook-tz-detected-${teamId}`}>
        · Detected from arrival times: {det.samples ? <b>{fmtOffset(det.detected_offset_min)}</b> : "waiting for pushes"}
        {det.samples ? ` (${det.samples} pushes, ${det.agreement_pct}% agree)` : ""}
      </span>
      {mismatch && (
        <Button size="sm" disabled={busy} onClick={() => apply(det.detected_offset_min)} data-testid={`webhook-tz-apply-${teamId}`}>
          Use {fmtOffset(det.detected_offset_min)}
        </Button>
      )}
      <select className="glass-input h-7 text-[11px] ml-auto" value="" disabled={busy}
              onChange={(e) => e.target.value !== "" && apply(Number(e.target.value))} data-testid={`webhook-tz-select-${teamId}`}>
        <option value="">Change…</option>
        {TZ_CHOICES.filter((o) => o !== cur).map((o) => <option key={o} value={o}>{fmtOffset(o)}{o === 330 ? " (IST)" : o === 480 ? " (China)" : ""}</option>)}
      </select>
    </div>
  );
}
